import { useState } from "react";
import { ChevronDown, Lock, Paperclip, Unlock } from "lucide-react";
import isString from "lodash/isString";

import { Button } from "@/components/ui/button";
import { selectColumn } from "components/BaseTableV3/selectColumn";
import ACL_RELATIONSHIPS from "acl-relationships";
import Authorized from "components/Authorized";
import RevenueOpportunityStatusDialog from "components/RevenueOpportunitiesTableV3/dialogs/RevenueOpportunityStatusDialog";
import TagList from "components/TagList";
import { buildPrice } from "components/helpers";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import EntityService from "services/Entity";
import CampaignService from "services/Campaign";
import RevenueOpportunityService from "services/RevenueOpportunities";
import CaseService from "services/Case";
import ContactService from "services/Contact";
import RecipientService from "services/Recipient";
import ProductService from "services/Product";
import PointOfContactService from "services/PointOfContact";
import { REVENUE_OPPORTUNITIES_TABLE_CONSTANTS } from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableV3";
import { RevenueOpportunitiesTableActions } from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableActions";
import CONFIG from "Config";
import { getCountryByCode } from "helpers/countries";
import { NULLABLE_OPERATORS } from "components/NewAdvancedSearch/filters/selectOperators";

const AttachmentIcon = ({ s3Urls, opportunityId }) => {
  const handleClick = async () => {
    await RevenueOpportunityService.getRevenueOpportunityAttachments(
      opportunityId
    );
  };

  if (!s3Urls || s3Urls.length === 0) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label="Download attachments"
      onClick={handleClick}
    >
      <Paperclip />
    </Button>
  );
};

const StatusCell = ({ revenueOpportunity, invalidateData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const statusName = revenueOpportunity.status_name;

  return (
    <div className="tw:flex tw:items-center tw:gap-1">
      {isString(statusName) ? (
        <strong>{statusName}</strong>
      ) : (
        <span className="tw:text-muted-foreground">(unset)</span>
      )}
      {!revenueOpportunity.is_locked && (
        <Authorized
          permissions={[ACL_RELATIONSHIPS.revenueOpportunityStatus.edit]}
        >
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Change status"
            onClick={() => setIsOpen(true)}
          >
            <ChevronDown />
          </Button>
          <RevenueOpportunityStatusDialog
            open={isOpen}
            onOpenChange={setIsOpen}
            revenueOpportunityIds={[revenueOpportunity.id]}
            campaignId={revenueOpportunity.campaign_id}
            statusId={revenueOpportunity.status_id}
            effectiveDate={revenueOpportunity.effective_date}
            onSaved={invalidateData}
          />
        </Authorized>
      )}
    </div>
  );
};

function getLineItemDefault(item) {
  const productName = item.product_name;
  const amount = buildPrice(Number(item.amount));
  const term = item.term;
  const quantity = item.quantity;
  const licenseStartDate = item.license_start_date;
  let value = `${productName} - ${amount} - ${term} - ${quantity}`;
  if (licenseStartDate) {
    value += ` - ${licenseStartDate}`;
  }
  return value;
}

const getItem = item => {
  let mapping = {
    is_penalty: `Penalty - ${buildPrice(Number(item.amount))}`,
    is_discount: `Discount - ${buildPrice(Number(item.amount))}`,
    is_total_value_override: `Total Value Override - ${buildPrice(
      Number(item.amount)
    )}`,
    default: getLineItemDefault(item),
  };
  for (let key in mapping) {
    if (item[key]) {
      return mapping[key];
    }
  }
  return mapping.default;
};

const totalCostColumns = isPricingEnabled =>
  isPricingEnabled
    ? [
        {
          header: "Total Cost",
          id: "Total Cost",
          accessorKey: "total_cost",
          // Blank, not N/A or $0.00, when no line has a cost.
          cell: ({ row }) => {
            const totalCost = row.original?.total_cost;
            return totalCost == null ? "" : buildPrice(Number(totalCost));
          },
        },
      ]
    : [];

export const getRevenueOpportunitiesTableColumns = ({
  invalidateData,
  deleteRevenueOpportunity,
  isPricingEnabled = false,
}) => {
  return [
    selectColumn,
    {
      header: "Opportunity ID",
      id: "Opportunity ID",
      accessorKey: "id",
      cell: ({ row }) => (
        <TableLink
          href={`/revenue_opportunities/${row.original.id}/activityLog`}
        >
          {row.original.id}
        </TableLink>
      ),
    },
    {
      header: "Lock State",
      id: "Locked",
      accessorKey: "is_locked",
      size: 80,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.is_locked ? (
          <Lock
            aria-label="Locked"
            className="tw:size-4 tw:text-muted-foreground"
          />
        ) : (
          <Unlock
            aria-label="Unlocked"
            className="tw:size-4 tw:text-muted-foreground"
          />
        ),
    },
    {
      header: "Assignee",
      id: "Assignee",
      accessorKey: "assignee_name",
    },
    {
      header: "Points of Contact",
      id: "Points of Contact",
      accessorKey: "points_of_contact_display",
      cell: ({ row }) => {
        const pointsOfContact = row.original?.points_of_contact ?? [];
        if (!pointsOfContact.length) return null;
        return (
          <ScrollColumn>
            {pointsOfContact.map(poc => {
              const roles = [
                poc.is_launch_contact && "Launch",
                poc.is_reporting_contact && "Reporting",
              ].filter(Boolean);
              return (
                <li key={poc.point_of_contact_id}>
                  {poc.point_of_contact_name}
                  {roles.length ? ` (${roles.join(", ")})` : ""}
                </li>
              );
            })}
          </ScrollColumn>
        );
      },
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.original.tags}
          modelType={REVENUE_OPPORTUNITIES_TABLE_CONSTANTS.className}
          modelId={row.original.id}
          onUpdate={invalidateData}
          tableCell
        />
      ),
    },
    {
      header: "Entity ID",
      id: "Entity ID",
      accessorKey: "entity_id",
      cell: ({ row }) => {
        return (
          <TableLink href={`/entities/${row.original.entity_id}`}>
            {row.original.entity_id}
          </TableLink>
        );
      },
    },
    {
      header: "Entity",
      id: "Entity",
      accessorKey: "entity_name",
      cell: ({ row }) => {
        return (
          <TableLink href={`/entities/${row.original.entity_id}`}>
            {row.original.entity_name}
          </TableLink>
        );
      },
    },
    {
      header: "External Case ID",
      id: "External Case ID",
      accessorKey: "cases",
      sortKey: "case.case_id",
      cell: ({ row }) => {
        const cases = row.original?.cases ?? [];
        if (!cases.length) return null;
        return (
          <ScrollColumn>
            {cases.map(c => (
              <li key={c.id}>
                <TableLink href={`/cases/${c.id}`}>{c.case_id}</TableLink>
              </li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "Case Name",
      id: "Case Name",
      accessorKey: "cases",
      sortKey: "case.name",
      cell: ({ row }) => {
        const cases = row.original?.cases ?? [];
        if (!cases.length) return null;
        return (
          <ScrollColumn>
            {cases.map(c => (
              <li key={c.id}>
                <TableLink href={`/cases/${c.id}`}>{c.name}</TableLink>
              </li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "Contact ID",
      id: "Contact ID",
      accessorKey: "contact.id",
      cell: ({ row }) => {
        if (row.original.contact?.id) {
          return (
            <TableLink href={`/contacts/${row.original.contact.id}`}>
              {row.original.contact.id}
            </TableLink>
          );
        }
        return null;
      },
    },
    {
      header: "Contact",
      id: "Contact",
      accessorKey: "contact.full_name",
      cell: ({ row }) => {
        if (
          row.original.contact &&
          row.original.contact.full_name &&
          row.original.contact.id
        ) {
          return (
            <TableLink href={`/contacts/${row.original.contact.id}`}>
              {row.original.contact.full_name}
            </TableLink>
          );
        }
        return null;
      },
    },
    {
      header: "Campaign",
      id: "Campaign",
      accessorKey: "campaign_name",
      cell: ({ row }) => {
        return row.original.campaign_name ? (
          <TableLink href={`/campaigns/${row.original.campaign_id}`}>
            {row.original.campaign_name}
          </TableLink>
        ) : null;
      },
    },
    {
      header: "Recipient Type",
      id: "Recipient Type",
      accessorKey: "recipient_type",
    },
    {
      header: "Recipient Name",
      id: "Recipient Name",
      accessorKey: "recipient_name",
    },
    { header: "Billing To", id: "Billing To", accessorKey: "billing_to" },
    {
      header: "P.O. #",
      id: "P.O. #",
      accessorKey: "purchase_order",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.purchase_order;
        if (!value?.length) return "N/A";
        return (
          <ScrollColumn>
            {value.map((listItem, index) => (
              <li key={`purchase-order-${row.original.id}-${index}`}>
                {listItem}
              </li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "Invoice #",
      id: "Invoice #",
      accessorKey: "invoice",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.invoice;
        if (!value?.length) return "N/A";
        return (
          <ScrollColumn>
            {value.map((listItem, index) => (
              <li key={`invoice-num-${row.original.id}-${index}`}>
                {listItem}
              </li>
            ))}
          </ScrollColumn>
        );
      },
    },
    { header: "Invoice Date", id: "Invoice Date", accessorKey: "invoice_date" },
    {
      header: "S.O. #",
      id: "S.O. #",
      accessorKey: "sales_order",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.sales_order;
        if (!value?.length) return "N/A";
        return (
          <ScrollColumn>
            {value.map((listItem, index) => (
              <li key={`sales-order-${row.original.id}-${index}`}>
                {listItem}
              </li>
            ))}
          </ScrollColumn>
        );
      },
    },
    { header: "S.O. Date", id: "S.O. Date", accessorKey: "sales_order_date" },
    {
      header: "Region",
      id: "Region",
      accessorKey: "region_name",
    },
    {
      header: "Country",
      id: "Country",
      accessorKey: "country",
      cell: ({ row }) => (
        <div>{getCountryByCode(row.getValue("Country"))?.text}</div>
      ),
    },
    {
      header: "Status",
      id: "Status",
      accessorKey: "status_name",
      cell: ({ row }) => (
        <StatusCell
          revenueOpportunity={row.original}
          invalidateData={invalidateData}
        />
      ),
    },
    {
      header: "Effective Date",
      id: "Effective Date",
      accessorKey: "effective_date",
      cell: ({ row }) => {
        const value = row.original?.effective_date;
        return value || "N/A";
      },
    },

    {
      header: "Launch Date",
      id: "Launch Date",
      accessorKey: "launch_date",
    },
    ...(CONFIG.ENABLE_PIRACY
      ? [
          {
            header: "Actionable Machines",
            id: "Actionable Machines",
            accessorKey: "actionable_machines",
          },
        ]
      : []),
    { header: "Comments", id: "Comments", accessorKey: "comments" },
    {
      header: "Line Items",
      id: "Line Items",
      accessorKey: "invoices",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.invoices;
        return (
          <ScrollColumn>
            {value?.map((listItem, index) => (
              <li key={`invoice-${row.original.id}-${index}`}>
                {getItem(listItem)}
              </li>
            ))}
          </ScrollColumn>
        );
      },
    },
    {
      header: "Total Value",
      id: "Total Value",
      accessorKey: "value",
      cell: ({ row }) => {
        const value = row.original?.value;
        return typeof value === "number" ? buildPrice(value) : "N/A";
      },
    },
    ...totalCostColumns(isPricingEnabled),
    {
      header: "Attachments",
      id: "Attachments",
      accessorKey: "attachments",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.attachments;
        const id = row.original?.id;
        return <AttachmentIcon s3Urls={value} opportunityId={id} />;
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      size: 60,
      enableSorting: false,
      enableResizing: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <RevenueOpportunitiesTableActions
          original={row.original}
          deleteRevenueOpportunity={deleteRevenueOpportunity}
        />
      ),
    },
  ];
};

export const getRevenueOpportunitiesTableFilters = ({
  tags,
  revenueOpportunityAssigneeOptions,
  purchaseOrdersOptions,
  salesOrdersOptions,
  invoicesOptions,
  recipientTypeOptions,
  statusOptions,
  entityId,
  contactId,
  regions = [],
  countries = [],
}) => {
  return [
    ...(!entityId
      ? [
          {
            key: "entity_id",
            title: "Entity",
            type: "ruvixxSelect",
            queryFn: EntityService.getEntitiesForFilters,
            props: {
              includeNoneOption: true,
            },
          },
        ]
      : []),
    {
      key: "campaign_id",
      title: "Campaign",
      type: "ruvixxSelect",
      queryFn: async () =>
        await CampaignService.getCampaignsForFilters({
          has_revenue_opportunities: true,
        }),
      props: {
        includeNoneOption: true,
      },
    },
    ...(CONFIG.ENABLE_PIRACY
      ? [
          {
            key: "case_id",
            title: "Case",
            type: "ruvixxSelect",
            queryFn: CaseService.getForFilters,
            className: filterParams =>
              filterParams?.["Match all selected cases"]
                ? "and-filter"
                : "or-filter",
            props: {
              includeNoneOption: true,
            },
          },
          {
            key: "match_all_cases",
            title: "Match all selected cases",
            type: "ruvixxToggle",
            hidden: filterParams => {
              const selected = filterParams?.Case;
              return !Array.isArray(selected) || selected.length === 0;
            },
          },
        ]
      : []),
    ...(!contactId
      ? [
          {
            key: "contact_id",
            title: "Contact",
            type: "ruvixxSelect",
            queryFn: ContactService.getContactsForFilters,
          },
        ]
      : []),
    {
      key: "status_id",
      title: "Status",
      type: "select",
      data: statusOptions,
    },
    {
      key: "effective_date",
      title: "Effective Date",
      type: "dateRange",
    },
    {
      key: "recipient_type",
      title: "Recipient Type",
      type: "select",
      data: recipientTypeOptions,
    },
    {
      key: "recipient_id",
      title: "Recipient Name",
      type: "ruvixxSelect",
      queryFn: RecipientService.getForFilters,
    },
    {
      key: "purchase_order_num",
      title: "P.O.#",
      type: "select",
      data: purchaseOrdersOptions,
    },
    {
      key: "sales_order_num",
      title: "S.O.#",
      type: "select",
      data: salesOrdersOptions,
    },
    {
      key: "sales_order_date",
      title: "S.O. Date",
      type: "dateRange",
    },
    {
      key: "invoice_num",
      title: "Invoice #",
      type: "select",
      data: invoicesOptions,
    },
    {
      key: "invoice_date",
      title: "Invoice Date",
      type: "dateRange",
    },
    {
      key: "product_id",
      title: "Product",
      type: "ruvixxSelect",
      queryFn: ProductService.getForFilters,
    },
    {
      key: "points_of_contact",
      title: "Point of Contact",
      type: "ruvixxSelect",
      queryFn: PointOfContactService.getForFilters,
    },
    {
      key: "value",
      title: "Value",
      type: "range",
    },
    {
      key: "launch_date",
      title: "Launch Date",
      type: "dateRange",
    },
    ...(CONFIG.ENABLE_PIRACY
      ? [
          {
            key: "actionable_machines",
            title: "Actionable Machines",
            type: "range",
          },
        ]
      : []),
    {
      key: "license_start_date",
      title: "License Start Date",
      type: "dateRange",
    },
    {
      key: "license_end_date",
      title: "License End Date",
      type: "dateRange",
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    {
      key: "assignee_id",
      title: "Revenue Opportunity Assignee",
      type: "select",
      data: revenueOpportunityAssigneeOptions,
    },
    {
      key: "is_locked",
      title: "Lock State",
      type: "select",
      data: [
        { id: "locked", name: "Locked" },
        { id: "unlocked", name: "Unlocked" },
      ],
      isSingle: true,
      clearable: true,
    },
    {
      key: "regions",
      title: "Region",
      type: "select",
      operators: NULLABLE_OPERATORS,
      data: regions,
    },
    {
      key: "countries",
      title: "Country",
      type: "select",
      operators: NULLABLE_OPERATORS,
      data: countries,
    },
  ];
};
